import React, { useState } from "react";

import { useSearchParams, useNavigate, Link } from "react-router-dom";

import { ShieldCheck, Lock, ArrowLeft, CheckCircle2 } from "lucide-react";

import { api, formatApiError } from "../lib/api";

import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const token = searchParams.get("token");

  const [password, setPassword] = useState("");

  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState(false);

  const [error, setError] = useState("");

  // ==================================================
  // INVALID TOKEN
  // ==================================================

  if (!token) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
        <div className="card-soft p-8 w-full max-w-md text-center">
          <h1 className="font-display text-2xl font-bold">
            Invalid reset link
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            This password reset link is invalid.
          </p>

          <Link to="/login" className="btn-primary mt-6 inline-flex">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  // ==================================================
  // SUBMIT
  // ==================================================

  const onSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");

      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");

      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });

      setSuccess(true);

      toast.success("Password updated successfully");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* BACK */}

        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 mb-6"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to login
        </Link>

        {/* CARD */}

        <div className="card-soft p-8">
          {/* HEADER */}

          <div className="flex items-center gap-3 mb-6">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
              <ShieldCheck className="h-5 w-5" />
            </div>

            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">
                AccessWealth
              </div>

              <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                Reset password
              </h1>
            </div>
          </div>

          {/* SUCCESS */}

          {success ? (
            <div className="text-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />

              <h2 className="mt-4 font-display text-2xl font-bold">
                Password updated
              </h2>

              <p className="mt-2 text-sm text-slate-500">
                Your password has been changed successfully.
              </p>

              <button
                onClick={() => navigate("/login")}
                className="btn-primary w-full mt-6"
              >
                Continue to login
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              {/* PASSWORD */}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  New password
                </label>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-soft !pl-10"
                    placeholder="Enter new password"
                  />
                </div>
              </div>

              {/* CONFIRM */}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Confirm password
                </label>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-soft !pl-10"
                    placeholder="Confirm password"
                  />
                </div>
              </div>

              {/* ERROR */}

              {error && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 text-rose-700 dark:text-rose-300 text-xs p-3">
                  {error}
                </div>
              )}

              {/* SUBMIT */}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? "Updating..." : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
